import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const FUNDS = ["general","scholarships","food_forest","construction","land_expansion"];

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get("/campaigns"); setItems(data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!confirm("Delete campaign?")) return; await api.delete(`/campaigns/${id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Funding · Campaigns</div>
          <h1 className="font-serif text-4xl mt-1">Current Campaigns</h1>
        </div>
        <NewCampaign onCreated={load} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map(c => {
          const pct = c.goal > 0 ? Math.min(100, Math.round((c.raised/c.goal)*100)) : 0;
          return (
            <div key={c.id} className="bg-admin-surface border border-line rounded-xl p-6" data-testid={`campaign-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-serif text-2xl text-ink">{c.name}</div>
                  <div className="overline mt-1">Fund: {c.fund.replace("_"," ")}</div>
                </div>
                <button onClick={() => del(c.id)} className="text-ink-soft hover:text-terracotta"><Trash2 className="h-4 w-4" /></button>
              </div>
              <p className="text-ink-soft text-sm mt-3">{c.description}</p>
              <div className="mt-4 flex justify-between text-sm">
                <span className="text-ink">${(c.raised || 0).toFixed(0)} raised</span>
                <span className="text-ink-soft">Goal ${(c.goal || 0).toFixed(0)} · {c.donor_count || 0} donors</span>
              </div>
              <div className="h-2 bg-line rounded-full overflow-hidden mt-2"><div className="h-full bg-forest" style={{ width: `${pct}%` }} /></div>
              <div className="text-xs text-terracotta mt-2 font-medium">{pct}% funded</div>
            </div>
          );
        })}
        {!items.length && <div className="text-ink-soft text-sm col-span-full py-10 text-center">No campaigns yet.</div>}
      </div>
    </div>
  );
}

function NewCampaign({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", fund: "general", goal: "", starts: "", ends: "", status: "active", description: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/campaigns", f); toast.success("Campaign created"); setOpen(false); onCreated(); setF({ name: "", fund: "general", goal: "", starts: "", ends: "", status: "active", description: "" }); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="pill pill-primary text-sm" data-testid="new-campaign-btn"><Plus className="h-4 w-4" /> New Campaign</button></DialogTrigger>
      <DialogContent className="bg-bone">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Campaign</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="overline">Name</Label><Input required data-testid="nc-name" value={f.name} onChange={e=>setF({...f, name: e.target.value})} /></div>
          <div><Label className="overline">Fund</Label>
            <Select value={f.fund} onValueChange={v => setF({...f, fund: v})}>
              <SelectTrigger data-testid="nc-fund"><SelectValue /></SelectTrigger>
              <SelectContent>{FUNDS.map(v => <SelectItem key={v} value={v}>{v.replace("_"," ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Goal (USD)</Label><Input type="number" min="0" data-testid="nc-goal" value={f.goal} onChange={e=>setF({...f, goal: e.target.value})} /></div>
            <div><Label className="overline">Status</Label>
              <Select value={f.status} onValueChange={v => setF({...f, status: v})}>
                <SelectTrigger data-testid="nc-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="overline">Description</Label><Textarea rows={3} data-testid="nc-desc" value={f.description} onChange={e=>setF({...f, description: e.target.value})} /></div>
          <button type="submit" data-testid="nc-submit" className="pill pill-primary">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
